import React, { useState } from 'react';

import ParticleBackground from '../../components/ParticleBackground';

import { globalAnimationStyles } from '../../styles/theme';
import {
  getCommonContainerStyles,
  getH1Style,
} from '../../styles/commonStyles';
import {
  useMobile,
  useScrollToTop,
  useThemeCssVars,
} from '../../styles/commonHooks';
import { getPrivacyPolicyText } from '../../translations/privacyPolicy';

export default function PrivacyPolicy({ lang, theme, isSamsungBrowser = false }) {
  const isMobile = useMobile();
  useScrollToTop();

  const [showAddress, setShowAddress] = useState(false);

  const themeVars = useThemeCssVars(theme, isSamsungBrowser);
  const containerStyles = getCommonContainerStyles(themeVars, isMobile);
  const h1Style = getH1Style(themeVars, isMobile, isSamsungBrowser);
  const text = getPrivacyPolicyText(lang);

  const styles = {
    main: {
      flex: 1,
      textAlign: 'left',
      lineHeight: '1.65',
      fontSize: isMobile ? '1.18rem' : '1.35rem',
    },
    h2: {
      fontSize: isMobile ? '1.7rem' : '1.9rem',
      fontWeight: '700',
      margin: '40px 0 20px',
      color: themeVars.fg,
    },
    h3: {
      fontSize: isMobile ? '1.35rem' : '1.5rem',
      fontWeight: '600',
      margin: '24px 0 12px',
    },
    updateDate: {
      fontSize: '1.1rem',
      fontStyle: 'italic',
      marginBottom: '32px',
      opacity: 0.9,
      textAlign: 'center',
    },
    link: {
      color: themeVars.accent,
      textDecoration: 'underline',
      fontWeight: '500',
    },
    addressToggle: {
      color: themeVars.accent,
      cursor: 'pointer',
      textDecoration: 'underline',
      fontWeight: '500',
    },
    hr: {
      border: `1px solid ${themeVars.accent}30`,
      margin: '32px 0',
    },
  };

  const ul = (items) => (
    <ul style={{ listStyleType: 'disc', paddingLeft: '24px' }}>
      {items.map((item, i) => <li key={i}>{item}</li>)}
    </ul>
  );

  const sections = [
    {
      delay: '0.08s',
      content: <h1 style={h1Style}>{text.pageTitle}</h1>,
    },
    {
      delay: '0.16s',
      content: <p style={styles.updateDate}>{text.lastUpdated}</p>,
    },
    {
      delay: '0.24s',
      content: <hr style={styles.hr} />,
    },
    {
      delay: '0.32s',
      content: (
        <>
          <h2 style={styles.h2}>{text.s1Title}</h2>
          <p><strong>{text.s1Controller}</strong></p>
          <p>
            Network Assistant<br />
            <span onClick={() => setShowAddress(!showAddress)} style={styles.addressToggle}>
              {showAddress ? '156 Cours Berriat 38000 Grenoble' : text.s1AddressToggle}
            </span>
            <br />
            {text.s1ContactEmail}{' '}
            <a href="mailto:info@networkassistant.ai" style={styles.link}>
              info@networkassistant.ai
            </a>
            <br />
            {text.s1DPO}{' '}
            <a href="mailto:info@networkassistant.ai" style={styles.link}>
              info@networkassistant.ai
            </a>
          </p>
          <p>{text.s1Body}</p>
        </>
      ),
    },
    {
      delay: '0.40s',
      content: (
        <>
          <hr style={styles.hr} />
          <h2 style={styles.h2}>{text.s2Title}</h2>
          <p>{text.s2Intro}</p>
          <h3 style={styles.h3}>{text.s21Title}</h3>
          {ul(text.s21Items)}
          <h3 style={styles.h3}>{text.s22Title}</h3>
          {ul(text.s22Items)}
        </>
      ),
    },
    {
      delay: '0.48s',
      content: (
        <>
          <hr style={styles.hr} />
          <h2 style={styles.h2}>{text.s3Title}</h2>
          {ul(text.s3Items)}
          <p>{text.s3Footer}</p>
        </>
      ),
    },
    {
      delay: '0.56s',
      content: (
        <>
          <hr style={styles.hr} />
          <h2 style={styles.h2}>{text.s4Title}</h2>
          <p>{text.s4Intro}</p>
          {ul(text.s4Items)}
          <p>{text.s4Footer}</p>
        </>
      ),
    },
    {
      delay: '0.64s',
      content: (
        <>
          <hr style={styles.hr} />
          <h2 style={styles.h2}>{text.s5Title}</h2>
          <p>{text.s5Intro}</p>
          {ul(text.s5Items)}
          <p>{text.s5Footer}</p>
        </>
      ),
    },
    {
      delay: '0.72s',
      content: (
        <>
          <hr style={styles.hr} />
          <h2 style={styles.h2}>{text.s6Title}</h2>
          <p>{text.s6Body}</p>
        </>
      ),
    },
    {
      delay: '0.80s',
      content: (
        <>
          <hr style={styles.hr} />
          <h2 style={styles.h2}>{text.s7Title}</h2>
          <p>{text.s7Body}</p>
        </>
      ),
    },
    {
      delay: '0.88s',
      content: (
        <>
          <hr style={styles.hr} />
          <h2 style={styles.h2}>{text.s8Title}</h2>
          <p>{text.s8Intro}</p>
          {ul(text.s8Items)}
        </>
      ),
    },
  ];

  return (
    <>
      <ParticleBackground theme={theme} />

      <div style={containerStyles.outer}>
        <div style={containerStyles.inner}>
          <main key={lang} style={styles.main}>
            {sections.map((section, index) => (
              <div
                key={index}
                className="animate-on-mount"
                style={{ animationDelay: section.delay }}
              >
                {section.content}
              </div>
            ))}
          </main>
        </div>
      </div>

      <style jsx global>{globalAnimationStyles}</style>
    </>
  );
}